'use client'

import type { Allocation } from '@moment/horizon-data-access/proposal'
import type { ProposalDetailsQueryParams } from '@/app/(dashboard)/proposals/[id]/page'
import type { RootState } from '@/app/store'

import { useMemo, useState } from 'react'

import TableBasic from '@/components/atomic/table-basic'
import ConfirmAllocationRemoveDialog from '@/components/molecules/confirm-proposal-allocation-remove-dialog'
import useTieredSelector from '@/hooks/store/useTieredSelector'
import { useGetAbbreviatedAccountListQuery } from '@/legacy-api/accounts-slice'
import { useGetProposalQuery } from '@/legacy-api/proposals-slice'

import { getColumns } from './columns'
import { useDeleteAllocation } from './helpers'

interface ProposalAllocationTableProps {
  allocations: Allocation[]
  onUpdateAllocation: (allocationId: string, quantity: number) => void
  proposalId: string
  disableQuantityEditing?: boolean
  hideCancelAllocation?: boolean
}

interface TradeDetails {
  id: string
  name: string
  accountName: string
  percentage: string
}

// create trade details for deletion dialog
function createTradeDetails(allocation: Allocation): TradeDetails {
  return {
    id: allocation.proposal_allocation_id,
    name: allocation.moment_account_id,
    accountName: allocation.moment_account_id,
    percentage: '',
  }
}

export function ProposalAllocationTable({
  allocations,
  onUpdateAllocation,
  proposalId,
  disableQuantityEditing = false,
  hideCancelAllocation = false,
}: ProposalAllocationTableProps) {
  const [selectedAllocation, setSelectedAllocation] =
    useState<Allocation | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const selectedOrder = useTieredSelector(
    (state: RootState) => state.proposals.selectedOrder,
    (params: ProposalDetailsQueryParams) => params.selectedOrder,
  )

  const selectedAccount = useTieredSelector(
    (state: RootState) => state.proposals.selectedAccount,
    (params: ProposalDetailsQueryParams) => params.selectedAccount,
  )

  const { data: proposal } = useGetProposalQuery({ proposal_id: proposalId })
  const { data: accounts } = useGetAbbreviatedAccountListQuery({
    filters: {
      moment_account_id: {
        in: allocations.map((allocation) => allocation.moment_account_id),
      },
    },
    include_sleeves: false,
    include_statistics: false,
    include_margin_fields: true,
  })

  // Create a mapping of account IDs to account names
  const accountNameMap = useMemo(() => {
    if (!accounts) return {}
    return accounts.data.accounts.accounts.reduce(
      (acc, account) => {
        acc[account.moment_account_id] = account.account_name || ''
        return acc
      },
      {} as Record<string, string>,
    )
  }, [accounts])

  const proposalStatus = proposal?.proposal_status

  // Handlers
  const handleDelete = useDeleteAllocation()

  const handleDeleteClick = (allocation: Allocation) => {
    setSelectedAllocation(allocation)
    setIsDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!selectedAllocation || !selectedOrder) return

    await handleDelete({
      proposalId,
      proposalOrderId: selectedOrder,
      proposalAllocationId: selectedAllocation.proposal_allocation_id,
    })

    setIsDialogOpen(false)
    setSelectedAllocation(null)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: Bulk suppression as part of biome migration
  const columns = useMemo(
    () =>
      getColumns({
        onUpdateAllocation,
        onDeleteClick: handleDeleteClick,
        selectedAccount,
        proposalStatus,
        accountNameMap,
        disableQuantityEditing,
        hideCancelAllocation,
      }),
    [
      onUpdateAllocation,
      selectedAccount,
      proposalStatus,
      accountNameMap,
      disableQuantityEditing,
      hideCancelAllocation,
    ],
  )

  // Sort allocations to put selected account first
  const sortedAllocations = useMemo(() => {
    if (!selectedAccount) return allocations

    return [...allocations].sort((a, b) => {
      if (a.moment_account_id === selectedAccount) return -1
      if (b.moment_account_id === selectedAccount) return 1
      return 0
    })
  }, [allocations, selectedAccount])

  return (
    <>
      <TableBasic
        key={selectedOrder}
        columns={columns}
        data={sortedAllocations}
      />

      {selectedAllocation && (
        <ConfirmAllocationRemoveDialog
          open={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          onConfirm={handleConfirmDelete}
          tradeDetails={createTradeDetails(selectedAllocation)}
        />
      )}
    </>
  )
}
